import {
  useAssessmentTypesQuery,
  useCreateAssessmentAndLinkMutation,
  useCreateAssessmentMutation,
} from '@common/cache';
import { paths } from '@common/types';
import React from 'react';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';

export const useCreateAssessment = (
  patient_id: string,
  setItemEditId: (id: string) => void,
) => {
  const createMutation = useCreateAssessmentMutation(patient_id);
  const createLinkMutation = useCreateAssessmentAndLinkMutation(patient_id);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { data = [] } = useAssessmentTypesQuery();

  const assessmentTypeOptions = data.map(assessment_type => ({
    value: assessment_type.id,
    label: assessment_type.name,
    can_link_be_generated: assessment_type.can_link_be_generated,
  }));

  const handleCreate = async (type: string) => {
    try {
      const response = await createMutation.mutateAsync({
        type: type as paths['/patient/{patient_id}/assessment']['post']['requestBody']['content']['application/json']['type'],
      });

      if (response.data.id) {
        setItemEditId(response.data.id);
      }
      handleClose();
      toast.success('Assessment created successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleGenerateLink = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    type: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await createLinkMutation.mutateAsync({
        type: type as paths['/patient/{patient_id}/assessment-link']['post']['requestBody']['content']['application/json']['type'],
      });

      navigator.clipboard.writeText(response.data.link);
      handleClose();
      toast.success('Link to the assessment copied!');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    handleClick,
    handleClose,
    anchorEl,
    open,
    assessmentTypeOptions,
    handleCreate,
    isLoading: createMutation.isPending || createLinkMutation.isPending,
    handleGenerateLink,
  };
};
